/**
 * Sandbox Management Admin Page
 * Provision sandbox features (mock data, API access) for existing TRIAL organizations.
 * User invites are handled via the existing User Management page.
 */

'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, RefreshCw, Pause, RotateCcw, ArrowRightLeft, Clock, Users, Building2, Key, ChevronDown, Activity } from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import AccessActivityReport from '@/components/admin/AccessActivityReport';
import { trackAction } from '@/src/lib/activityTracker';
import { PageHeader, PageContainer } from '@/components/weavein';

interface TrialOrg {
  id: number;
  name: string;
  contact_email: string | null;
  status: string;
  has_active_sandbox: boolean;
  active_sandbox_id: number | null;
  created_at: string | null;
}

interface Sandbox {
  id: number;
  cbo_id: number;
  organization_name: string | null;
  customer_status: string | null;
  status: string;
  expires_at: string;
  extended_count: number;
  login_count: number;
  seed_profile: string;
  features_tier: string;
  api_sandbox_id: number | null;
  created_at: string;
  days_remaining: number | null;
}

export default function SandboxManagementPage() {
  const [view, setView] = useState<'sandboxes' | 'activity'>('sandboxes');
  const [sandboxes, setSandboxes] = useState<Sandbox[]>([]);
  const [trialOrgs, setTrialOrgs] = useState<TrialOrg[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');

  // Provision form
  const [selectedOrgId, setSelectedOrgId] = useState<number | ''>('');
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [provisionApiAccess, setProvisionApiAccess] = useState(false);
  const [enableMarketingSales, setEnableMarketingSales] = useState(false);

  // Expanded detail
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [dataSummary, setDataSummary] = useState<any>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Extend dialog
  const [extendTarget, setExtendTarget] = useState<Sandbox | null>(null);
  const [customExtendDays, setCustomExtendDays] = useState<number>(30);

  const apiUrl = getWeaveinApiUrl();

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    return {
      'Authorization': `Bearer ${token}`,
      'X-CBO-ID': cboId || '',
      'Content-Type': 'application/json',
    };
  }, []);

  const loadSandboxes = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter ? `${apiUrl}/api/sandboxes?status=${filter}` : `${apiUrl}/api/sandboxes`;
      const res = await fetch(url, { headers: getHeaders() });
      if (res.ok) setSandboxes(await res.json());
    } catch {
      setError('Failed to load sandboxes');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, filter, getHeaders]);

  const loadTrialOrgs = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/sandboxes/trial-organizations`, { headers: getHeaders() });
      if (res.ok) setTrialOrgs(await res.json());
    } catch {}
  }, [apiUrl, getHeaders]);

  const loadDataSummary = useCallback(async (sandboxId: number) => {
    setLoadingSummary(true);
    try {
      const res = await fetch(`${apiUrl}/api/sandboxes/${sandboxId}/data-summary`, { headers: getHeaders() });
      if (res.ok) setDataSummary(await res.json());
    } catch {} finally { setLoadingSummary(false); }
  }, [apiUrl, getHeaders]);

  const toggleExpand = (sandboxId: number) => {
    if (expandedId === sandboxId) { setExpandedId(null); setDataSummary(null); }
    else { setExpandedId(sandboxId); loadDataSummary(sandboxId); }
  };

  useEffect(() => { loadSandboxes(); }, [loadSandboxes]);
  useEffect(() => { if (showForm) loadTrialOrgs(); }, [showForm, loadTrialOrgs]);

  const provisionSandbox = async () => {
    if (!selectedOrgId) return;
    setProvisioning(true);
    setError(null);
    try {
      const res = await fetch(`${apiUrl}/api/sandboxes/provision`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          cbo_id: selectedOrgId,
          expires_in_days: expiresInDays,
          provision_api_access: provisionApiAccess,
          enable_marketing_sales: enableMarketingSales,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Provisioning failed');
      }
      const result = await res.json();
      let msg = `Sandbox provisioned for "${result.organization_name}".`;
      if (result.api_credentials) {
        msg += `\nAPI Key: ${result.api_credentials.api_key}\n(Save this now — it will not be shown again)`;
      }
      setSuccess(msg);
      trackAction('sandbox_provisioned', { org: result.organization_name, with_api: provisionApiAccess });
      setShowForm(false);
      setSelectedOrgId('');
      setProvisionApiAccess(false);
      loadSandboxes();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setProvisioning(false);
    }
  };

  const extendSandbox = async (id: number, days: number) => {
    if (!Number.isFinite(days) || days < 1 || days > 90) {
      setError('Extension must be between 1 and 90 days');
      return;
    }
    setExtendTarget(null);
    await sandboxAction(id, 'extend', { days });
  };

  const sandboxAction = async (id: number, action: string, body?: any) => {
    try {
      const res = await fetch(`${apiUrl}/api/sandboxes/${id}/${action}`, {
        method: 'POST',
        headers: getHeaders(),
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) throw new Error((await res.json()).detail || `${action} failed`);
      const result = await res.json();
      setSuccess(result.message || `${action} completed successfully`);
      loadSandboxes();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const statusColors: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    expired: 'bg-red-100 text-red-800',
    converted: 'bg-blue-100 text-blue-800',
    deactivated: 'bg-paper-2 text-ink-500',
  };

  const availableOrgs = trialOrgs.filter(o => !o.has_active_sandbox);

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Admin · Developer"
        title="Sandbox Management"
        description="Provision sandbox features for TRIAL organizations. Use User Management to invite users."
        actions={
          <>
            <button
              onClick={loadSandboxes}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-md border border-v2border bg-surface px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-paper-2 disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
            >
              <Plus className="h-3.5 w-3.5" /> Provision Sandbox
            </button>
          </>
        }
      />
      <PageContainer>

      {/* Section toggle: Sandboxes | Access & Activity */}
      <div className="inline-flex rounded-lg border border-v2border overflow-hidden">
        <button
          onClick={() => setView('sandboxes')}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium ${
            view === 'sandboxes' ? 'bg-amber-500 text-white' : 'bg-surface text-ink-500 hover:bg-paper'
          }`}
        >
          <Building2 className="w-4 h-4" /> Sandboxes
        </button>
        <button
          onClick={() => setView('activity')}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium ${
            view === 'activity' ? 'bg-amber-500 text-white' : 'bg-surface text-ink-500 hover:bg-paper'
          }`}
        >
          <Activity className="w-4 h-4" /> Access &amp; Activity
        </button>
      </div>

      {view === 'activity' ? (
        <AccessActivityReport trialOnly />
      ) : (
      <>

      {/* Extend Sandbox dialog */}
      {extendTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setExtendTarget(null)}>
          <div className="bg-surface rounded-xl shadow-xl w-full max-w-md p-6 space-y-4"
            onClick={e => e.stopPropagation()}>
            <div>
              <h3 className="text-lg font-semibold">Extend Sandbox</h3>
              <p className="text-sm text-ink-500 mt-1">
                {extendTarget.organization_name || `Org #${extendTarget.cbo_id}`}
                {extendTarget.days_remaining !== null && (
                  <span className={extendTarget.days_remaining < 0 ? 'text-red-600' : ''}>
                    {' · '}{extendTarget.days_remaining}d remaining
                  </span>
                )}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => extendSandbox(extendTarget.id, 14)}
                className="px-3 py-2 border rounded-lg text-sm font-medium hover:bg-paper">
                +14 days
              </button>
              <button onClick={() => extendSandbox(extendTarget.id, 30)}
                className="px-3 py-2 border rounded-lg text-sm font-medium hover:bg-paper">
                +30 days
              </button>
            </div>

            <div>
              <label className="block text-xs font-medium text-ink-500 mb-1">Custom (1–90 days)</label>
              <div className="flex gap-2">
                <input type="number" min={1} max={90} value={customExtendDays}
                  onChange={e => setCustomExtendDays(parseInt(e.target.value) || 0)}
                  onKeyDown={e => { if (e.key === 'Enter') extendSandbox(extendTarget.id, customExtendDays); }}
                  autoFocus
                  className="flex-1 border rounded-lg px-3 py-2 text-sm" />
                <Button onClick={() => extendSandbox(extendTarget.id, customExtendDays)}
                  className="bg-amber-500 hover:bg-amber-600 text-white">
                  Apply
                </Button>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setExtendTarget(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}
        <button onClick={() => setError(null)} className="ml-2 underline">dismiss</button></div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm whitespace-pre-wrap">{success}
        <button onClick={() => setSuccess(null)} className="ml-2 underline">dismiss</button></div>}

      {/* Provision Form */}
      {showForm && (
        <div className="bg-surface rounded-xl border p-6 space-y-4">
          <h2 className="font-semibold text-lg">Provision Sandbox for TRIAL Organization</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-ink-700 mb-1">Select TRIAL Organization *</label>
              {availableOrgs.length === 0 ? (
                <p className="text-sm text-ink-500 italic">
                  No eligible TRIAL organizations. Create an organization with customer_status = TRIAL first.
                </p>
              ) : (
                <div className="relative">
                  <select
                    value={selectedOrgId}
                    onChange={e => setSelectedOrgId(e.target.value ? parseInt(e.target.value) : '')}
                    className="w-full border rounded-lg px-3 py-2 text-sm appearance-none bg-surface pr-10"
                  >
                    <option value="">-- Select organization --</option>
                    {availableOrgs.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.name} (ID: {org.id}){org.contact_email ? ` — ${org.contact_email}` : ''}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="w-4 h-4 text-ink-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">Trial Duration (days)</label>
              <input type="number" value={expiresInDays} onChange={e => setExpiresInDays(parseInt(e.target.value) || 30)}
                min={7} max={90} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>

            <div className="space-y-3 pt-4 md:col-span-2">
              <div className="flex items-center gap-2">
                <input type="checkbox" id="api-access" checked={provisionApiAccess}
                  onChange={e => setProvisionApiAccess(e.target.checked)}
                  className="rounded border-v2border" />
                <label htmlFor="api-access" className="text-sm text-ink-700 flex items-center gap-1">
                  <Key className="w-4 h-4 text-ink-400" /> Provision API sandbox credentials
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="marketing-sales" checked={enableMarketingSales}
                  onChange={e => setEnableMarketingSales(e.target.checked)}
                  className="rounded border-v2border" />
                <label htmlFor="marketing-sales" className="text-sm text-ink-700 flex items-center gap-1">
                  <Building2 className="w-4 h-4 text-ink-400" /> Enable Marketing & Sales platform modules
                </label>
              </div>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={provisionSandbox} disabled={provisioning || !selectedOrgId}
              className="bg-amber-500 hover:bg-amber-600 text-white">
              {provisioning ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Provision Sandbox
            </Button>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2">
        {['', 'active', 'expired', 'converted', 'deactivated'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
              filter === f ? 'bg-gray-900 text-white' : 'bg-paper-2 text-ink-500 hover:bg-paper-2'
            }`}>
            {f || 'All'}
          </button>
        ))}
      </div>

      {/* Sandbox List */}
      <div className="bg-surface rounded-xl border overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-ink-500">Loading sandboxes...</div>
        ) : sandboxes.length === 0 ? (
          <div className="p-12 text-center text-ink-500">
            No sandboxes found. Click "Provision Sandbox" to set one up for a TRIAL organization.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-paper border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-ink-500 uppercase">Organization</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ink-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ink-500 uppercase">Days Left</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ink-500 uppercase">API Access</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ink-500 uppercase">Logins</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ink-500 uppercase">Created</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-ink-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sandboxes.map(s => (
                <React.Fragment key={s.id}>
                <tr className="hover:bg-paper cursor-pointer" onClick={() => toggleExpand(s.id)}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <ChevronDown className={`w-4 h-4 text-ink-400 transition-transform ${expandedId === s.id ? 'rotate-180' : ''}`} />
                      <div>
                        <div className="font-medium">{s.organization_name || `Org #${s.cbo_id}`}</div>
                        <div className="text-xs text-ink-500">CBO {s.cbo_id} · {s.customer_status}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[s.status] || 'bg-paper-2'}`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {s.days_remaining !== null ? (
                      <span className={`flex items-center gap-1 ${s.days_remaining <= 7 ? 'text-red-600 font-medium' : 'text-ink-500'}`}>
                        <Clock className="w-3 h-3" /> {s.days_remaining}d
                      </span>
                    ) : '-'}
                  </td>
                  <td className="px-4 py-3">
                    {s.api_sandbox_id ? (
                      <span className="inline-flex items-center gap-1 text-green-700 bg-green-50 px-2 py-0.5 rounded-full text-xs">
                        <Key className="w-3 h-3" /> Active
                      </span>
                    ) : (
                      <span className="text-ink-400 text-xs">None</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1 text-ink-500">
                      <Users className="w-3 h-3" /> {s.login_count}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-ink-500 text-xs">
                    {new Date(s.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                    {s.status === 'active' && (
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => { setCustomExtendDays(30); setExtendTarget(s); }}
                          title="Extend sandbox" className="p-1.5 rounded hover:bg-paper-2 text-ink-500">
                          <Clock className="w-4 h-4" />
                        </button>
                        <button onClick={async () => { await sandboxAction(s.id, 'reset'); if (expandedId === s.id) loadDataSummary(s.id); }}
                          title="Reset & re-seed data" className="p-1.5 rounded hover:bg-paper-2 text-ink-500">
                          <RotateCcw className="w-4 h-4" />
                        </button>
                        <button onClick={() => sandboxAction(s.id, 'convert')}
                          title="Convert to CUSTOMER" className="p-1.5 rounded hover:bg-blue-50 text-blue-600">
                          <ArrowRightLeft className="w-4 h-4" />
                        </button>
                        <button onClick={() => sandboxAction(s.id, 'deactivate')}
                          title="Deactivate" className="p-1.5 rounded hover:bg-red-50 text-red-500">
                          <Pause className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                {/* Expanded detail row */}
                {expandedId === s.id && (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 bg-paper border-t">
                      {loadingSummary ? (
                        <div className="text-sm text-ink-500 flex items-center gap-2">
                          <RefreshCw className="w-3 h-3 animate-spin" /> Loading data summary...
                        </div>
                      ) : dataSummary ? (
                        <div className="space-y-4">
                          {/* Programs */}
                          <div>
                            <h4 className="text-xs font-semibold text-ink-500 uppercase mb-2">Programs</h4>
                            {dataSummary.programs?.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {dataSummary.programs.map((p: any) => (
                                  <span key={p.id} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${p.is_active ? 'bg-blue-50 text-blue-700' : 'bg-paper-2 text-ink-500'}`}>
                                    {p.name} <span className="text-[10px] opacity-60">({p.type})</span>
                                  </span>
                                ))}
                              </div>
                            ) : <span className="text-xs text-ink-400">No programs seeded</span>}
                          </div>
                          {/* Data Counts */}
                          <div>
                            <h4 className="text-xs font-semibold text-ink-500 uppercase mb-2">Seeded Data</h4>
                            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                              {Object.entries(dataSummary.counts || {}).map(([key, val]) => (
                                <div key={key} className="bg-surface rounded-lg border px-3 py-2">
                                  <div className="text-lg font-bold text-ink">{val as number}</div>
                                  <div className="text-[10px] text-ink-500 capitalize">{key.replace(/_/g, ' ')}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                          {/* Reset button */}
                          {s.status === 'active' && (
                            <div className="pt-2">
                              <Button variant="outline" size="sm"
                                onClick={async () => { await sandboxAction(s.id, 'reset'); loadDataSummary(s.id); }}>
                                <RotateCcw className="w-3 h-3 mr-1" /> Reset & Re-seed All Data
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : <span className="text-xs text-ink-400">No data available</span>}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </>
      )}
      </PageContainer>
    </div>
  );
}
